import React, {useEffect, useState, useRef} from 'react';
import {HeaderBar} from '@/components/header/HeaderBar';
import {AppContainer} from 'components/app/AppContainer';
import SidebarMenu from 'components/sidebar/SidebarMenu';
import {Footer} from 'components/footer/Footer';
import {AppLogout} from '@/components/utils/AppLogout';

interface PageContainerProps {
    children: React.ReactNode;
}

export const PageContainer = React.memo(({children}: PageContainerProps) => {
    const [showBackdrop, setShowBackdrop] = useState(false);
    const showBackdropRef = useRef(false);

    useEffect(() => {
        showBackdropRef.current = showBackdrop;
    }, [showBackdrop]);

    useEffect(() => {
        const isMobile = window.innerWidth < 992;
        
        if (isMobile) {
            document.body.classList.remove('show-sidebar');
            setShowBackdrop(false);
        }

        let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
        let lastWidth = window.innerWidth;
        let lastShowSidebar = document.body.classList.contains('show-sidebar');

        const checkSidebar = () => {
            const isMobileNow = window.innerWidth < 992;
            const hasShowSidebar = document.body.classList.contains('show-sidebar');
            
            if (isMobileNow) {
                if (hasShowSidebar !== lastShowSidebar) {
                    setShowBackdrop(hasShowSidebar);
                    lastShowSidebar = hasShowSidebar;
                }
            } else {
                if (hasShowSidebar) {
                    document.body.classList.remove('show-sidebar');
                }
                if (showBackdropRef.current) {
                    setShowBackdrop(false);
                }
                lastShowSidebar = false;
            }
        };

        checkSidebar();

        const observer = new MutationObserver(() => {
            const hasShowSidebar = document.body.classList.contains('show-sidebar');
            if (hasShowSidebar !== lastShowSidebar && window.innerWidth < 992) {
                setShowBackdrop(hasShowSidebar);
                lastShowSidebar = hasShowSidebar;
            }
        });
        observer.observe(document.body, {
            attributes: true,
            attributeFilter: ['class']
        });

        let rafId: number | null = null;
        
        const handleResize = () => {
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
            
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            
            rafId = requestAnimationFrame(() => {
                const currentWidth = window.innerWidth;
                const widthChanged = Math.abs(currentWidth - lastWidth) > 10;
                
                if (widthChanged) {
                    resizeTimeout = setTimeout(() => {
                        const isMobileNow = currentWidth < 992;
                        const wasMobile = lastWidth < 992;
                        
                        if (!isMobileNow && wasMobile) {
                            document.body.classList.remove('show-sidebar');
                            setShowBackdrop(false);
                            lastShowSidebar = false;
                        } else if (isMobileNow) {
                            checkSidebar();
                        }
                        
                        lastWidth = currentWidth;
                    }, 150);
                }
            });
        };

        window.addEventListener('resize', handleResize, { passive: true });

        return () => {
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            observer.disconnect();
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleBackdropClick = () => {
        document.body.classList.remove('show-sidebar');
    };

    return (
        <div id="main-wrapper">
            {showBackdrop && (
                <div 
                    className="sidebar-backdrop"
                    onClick={handleBackdropClick}
                />
            )}
            <HeaderBar/>
            <aside className="left-sidebar">
                <SidebarMenu/>
            </aside>
            <div className="page-wrapper static-page">
                <AppLogout>
                    <AppContainer>
                        {children}
                    </AppContainer>
                </AppLogout>
            </div>
            <Footer/>
        </div>
    );  
}
);
